import React, { useEffect, useState } from 'react';
import { z } from 'zod';
import { ParameterDescriptor } from '@/redux/internal-services/agentsApi';
import { cn } from '@/utils/cn';
import { Modal as BaseModal } from '@/components/UI/Modal';
import { Badge, type BadgeProps } from '@/components/UI/Badge';
import { Button } from '@/components/UI/Button';
import { Switch } from '@/components/UI/Switch';
import { fieldClass } from '@/components/UI/fieldStyles';

// ── style constants ────────────────────────────────────────────────────────────

/** Single-line controls (input, select): pill. */
export const inputCls = fieldClass();
/** Multi-line controls (textarea): box shape so the pill's fixed height is not applied. */
export const areaCls = fieldClass({ shape: 'box' });

export const labelCls = 'block text-sm font-medium text-ui-text mb-1';

// ── helpers ────────────────────────────────────────────────────────────────────

export const uid = () => Math.random().toString(36).slice(2, 9);

export function relTime(iso?: string): string {
  if (!iso) return '';
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export function fmtDuration(sec?: number): string {
  if (sec == null) return '—';
  if (sec < 60) return `${sec}s`;
  return `${Math.floor(sec / 60)}m ${sec % 60}s`;
}

// No SAM dispatch ever reaches these: assessments/proposals render embedded inside the
// Workflow card (never their own module), and billing/jobs/transactions have no chat
// integration built yet. Offering them as screens (tool placement or prompt scoping) would be
// clutter with no effect.
export const UNREACHABLE_MODULES = new Set(['assessments', 'proposals', 'billing', 'jobs', 'transactions']);

// Canonical module order for the Modules tab and the prompt-preview selector — all known modules in
// display order. 'properties' and 'quick_quote' are retired here: they're merged into 'workflow'
// for SAM/tool/prompt scoping (see SCREEN_META's 'workflow' entry and api-service's
// module_aliases.py) — a legacy agent with its own screens.properties/.quick_quote or a
// condition_modules/topic_modules block still referencing those slugs keeps working (backend
// fallback), and the Modules tab still surfaces it (see ScreensTab.tsx's orphaned-legacy-slug union)
// until explicitly converted.
export const KNOWN_MODULES = [
  'tasks', 'meetings', 'my_todos', 'notes', 'communications', 'workflow',
  'contacts', 'users', 'roles', 'calendar', 'notifications',
  'assessments', 'proposals', 'billing', 'jobs', 'transactions',
];

// Version history lives on the server (it snapshots the WHOLE agent document on every save); nothing here builds or
// stores versions any more. What the editor needs is the draft meta below and the comparison view in VersionsTab.

/** Fields the draft response adds to the agent; kept out of the form so they are never sent back as configuration. */
export const DRAFT_META_FIELDS = [
  'head_version', 'head_saved_at', 'head_saved_by', 'published_version', 'published_version_id',
  'has_unpublished_changes', 'version_created', 'versions',
] as const;

/** Split a draft / imported agent into its configuration and the history bookkeeping the server owns. */
export function splitDraftMeta<T extends Record<string, unknown>>(doc: T): { config: Record<string, unknown>; meta: Record<string, unknown> } {
  const config: Record<string, unknown> = {};
  const meta: Record<string, unknown> = {};
  Object.entries(doc).forEach(([k, v]) => {
    if ((DRAFT_META_FIELDS as readonly string[]).includes(k)) meta[k] = v;
    else config[k] = v;
  });
  return { config, meta };
}

export function promptText(prompts?: Array<{ title?: string; prompt: string }>): string {
  return (prompts || []).map((p) => (p.title ? `# ${p.title}\n` : '') + p.prompt).join('\n\n');
}

export function lineDiff(a: string, b: string) {
  const x = (a || '').split('\n'), y = (b || '').split('\n');
  const m = x.length, n = y.length;
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = m - 1; i >= 0; i--)
    for (let j = n - 1; j >= 0; j--)
      dp[i][j] = x[i] === y[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
  const out: Array<{ t: 'same' | 'add' | 'del'; v: string }> = [];
  let i = 0, j = 0;
  while (i < m && j < n) {
    if (x[i] === y[j]) { out.push({ t: 'same', v: x[i] }); i++; j++; }
    else if (dp[i + 1][j] >= dp[i][j + 1]) { out.push({ t: 'del', v: x[i] }); i++; }
    else { out.push({ t: 'add', v: y[j] }); j++; }
  }
  while (i < m) out.push({ t: 'del', v: x[i++] });
  while (j < n) out.push({ t: 'add', v: y[j++] });
  return out;
}

// ── constants ──────────────────────────────────────────────────────────────────

export interface PromptVariable {
  token: string;
  label: string;       // full human-readable name (tooltip + lists)
  short: string;       // short label shown on the Insert chips
  group: 'User' | 'Tenant' | 'Session' | 'Time' | 'Lists';
  /** What the value is, for the tooltip. */
  hint?: string;
}

/** Row titles for the chip groups (the group ids are kept as stored/used elsewhere). */
export const PROMPT_VARIABLE_GROUPS: { id: PromptVariable['group']; title: string }[] = [
  { id: 'User', title: 'User' },
  { id: 'Tenant', title: 'Company' },
  { id: 'Session', title: 'Session' },
  { id: 'Time', title: 'Date & time' },
  { id: 'Lists', title: 'Company lists' },
];

// Every merge variable the prompt renderer provides (jinja_renderer.build_context + prompt_generation extras).
export const PROMPT_VARIABLES: PromptVariable[] = [
  { token: 'user.name',             label: 'User name',            short: 'Name',          group: 'User', hint: 'Who SAM is talking to' },
  { token: 'user.email',            label: 'User email',           short: 'Email',         group: 'User' },
  { token: 'user.id',               label: 'User id',              short: 'User id',       group: 'User', hint: 'Internal id; lets "my" / "me" resolve to this user' },
  { token: 'user.role',             label: 'User role',            short: 'Role',          group: 'User', hint: 'Role name, e.g. Tenant Admin' },
  { token: 'user.role_id',          label: 'User role id',         short: 'Role id',       group: 'User' },
  { token: 'user.timezone',         label: 'User timezone',        short: 'Timezone',      group: 'User', hint: "The user's browser timezone, e.g. Asia/Kolkata" },
  { token: 'user.client',           label: 'User device (mobile or web)', short: 'Device', group: 'User', hint: '"mobile" (phone/tablet shell) or "web" (desktop)' },
  { token: 'user.location',         label: 'User location',        short: 'Location',      group: 'User', hint: 'Coarse latitude,longitude when the user allowed it' },
  { token: 'user.latitude',         label: 'User latitude',        short: 'Latitude',      group: 'User' },
  { token: 'user.longitude',        label: 'User longitude',       short: 'Longitude',     group: 'User' },
  { token: 'tenant.name',           label: 'Company name',         short: 'Name',          group: 'Tenant' },
  { token: 'tenant.id',             label: 'Company id',           short: 'Company id',    group: 'Tenant' },
  { token: 'tenant.timezone',       label: 'Company timezone',     short: 'Timezone',      group: 'Tenant', hint: 'Business timezone set in onboarding' },
  { token: 'tenant.company_email',  label: 'Company email',        short: 'Email',         group: 'Tenant' },
  { token: 'tenant.company_address',label: 'Company address',      short: 'Address',       group: 'Tenant' },
  { token: 'tenant.website',        label: 'Company website',      short: 'Website',       group: 'Tenant' },
  { token: 'tenant.work_types',     label: 'Work types',           short: 'Work types',    group: 'Tenant', hint: 'What the company does, comma separated' },
  { token: 'tenant.service_regions',label: 'Service regions',      short: 'Regions',       group: 'Tenant', hint: 'Where the company works, comma separated' },
  // Per-turn values, for blocks that branch: {% if verbosity == 'concise' %} / {% if
  // interaction_mode == 'interactive' %}. `verbosity` is the agent's own model_parameters setting
  // (Overview tab), falling back to the tenant/user agent_chat config, then 'normal'.
  // `interaction_mode` is 'text' | 'push_to_talk' | 'interactive' — hands-free is not reported to
  // the backend, so it arrives as 'interactive'.
  { token: 'verbosity',             label: 'Response verbosity',   short: 'Verbosity',     group: 'Session', hint: 'concise | normal | detailed' },
  { token: 'interaction_mode',      label: 'Interaction mode',     short: 'Mode',          group: 'Session', hint: 'text | push_to_talk | interactive (voice)' },
  { token: 'agent_name',            label: 'Agent name',           short: 'Agent name',    group: 'Session' },
  { token: 'session_id',            label: 'Conversation id',      short: 'Conversation id', group: 'Session' },
  { token: 'first_chat_ever',       label: "User's first chat ever", short: 'First chat',  group: 'Session', hint: 'true the very first time this user talks to SAM (for the welcome)' },
  { token: 'current.local_time',  label: "Current local date and time", short: 'Local time',   group: 'Time', hint: "In the user's timezone, e.g. Friday, October 16, 2026, 03:17 PM" },
  { token: 'current.date',        label: "Today's date (YYYY-MM-DD)",    short: 'Today',        group: 'Time', hint: "In the user's timezone" },
  { token: 'current.weekday',     label: 'Today (weekday name)',         short: 'Weekday',      group: 'Time', hint: 'e.g. Friday' },
  { token: 'current.weekday_index', label: 'Weekday number (Monday=0)',  short: 'Weekday no.',  group: 'Time', hint: 'Monday=0 … Sunday=6' },
  { token: 'current.timezone',    label: 'Timezone used for "now"',      short: 'Timezone used', group: 'Time', hint: "The user's, else the company's, else UTC" },
  { token: 'current.utc_time',    label: 'Current UTC time',             short: 'UTC time',     group: 'Time' },
  { token: 'engagement_types_display',          label: 'Job types (names)',            short: 'Job types',        group: 'Lists', hint: "The company's engagement types, names only" },
  { token: 'engagement_types_detailed',         label: 'Job types (with details)',     short: 'Job types + details', group: 'Lists' },
  { token: 'engagement_entry_points_display',   label: 'Lead sources (names)',         short: 'Lead sources',     group: 'Lists', hint: 'How jobs enter the pipeline' },
  { token: 'engagement_entry_points_detailed',  label: 'Lead sources (with details)',  short: 'Lead sources + details', group: 'Lists' },
  { token: 'engagement_stages_statuses_block',  label: 'Job stages and statuses',      short: 'Stages & statuses', group: 'Lists' },
  { token: 'contact_relationship_types_display',  label: 'Contact relationships (names)',        short: 'Relationships', group: 'Lists' },
  { token: 'contact_relationship_types_detailed', label: 'Contact relationships (with details)', short: 'Relationships + details', group: 'Lists' },
  { token: 'contact_capacities_display',        label: 'Contact roles on a job (names)',        short: 'Contact roles', group: 'Lists' },
  { token: 'contact_capacities_detailed',       label: 'Contact roles on a job (with details)', short: 'Contact roles + details', group: 'Lists' },
];

// ── Zod schema + form type ─────────────────────────────────────────────────────

const systemPromptSchema = z.object({
  id: z.string().optional(),
  title: z.string().optional(),
  prompt: z.string(),
  order: z.number().optional(),
  category: z.string().optional(),
  // roles that see this block; empty / undefined = always injected
  condition_roles: z.array(z.string()).optional(),
  // Legacy: screens (SamModule slugs) this block is scoped to, re-evaluated only ONCE at realtime
  // connect (goes stale if the user changes screens mid voice-call). Kept for round-trip + legacy
  // detection (see promptPlacement.ts) — no editing UI writes this anymore; use topic_modules.
  condition_modules: z.array(z.string()).optional(),
  // Screens (SamModule slugs) this block is ABOUT — re-evaluated every turn/every session.update,
  // so it never goes stale. Exactly one module + no home_module = that screen's "Specific" block
  // (Modules tab), only included while that screen is active.
  topic_modules: z.array(z.string()).optional(),
  // Card ids ("engagement.view") and sub-card keys ("engagement.view#proposal") this block is ABOUT — the
  // same gate one level down, only included while that card / section is the active one (Modules tab →
  // the module's "Cards & sections"). See promptPlacement.ts.
  topic_cards: z.array(z.string()).optional(),
  // A condition over the `screen` variable (e.g. "screen.open('billing')"): when set it decides alone whether the block is
  // sent, and `tools` are offered exactly while it holds. Validated on the server (cards/conditions.py).
  applies_when: z.string().nullish(), // the server stores a cleared condition as null
  tools: z.array(z.string()).optional(),
  // Marks this block as the named module's "General" prompt (Modules tab) — organizationally tied
  // to one screen, but functionally unconditional: included every turn regardless of active screen,
  // same as a block with no scoping at all. See promptPlacement.ts.
  home_module: z.string().optional(),
});

export type PromptCategory = 'persona' | 'rules' | 'context' | 'format' | 'tools' | string;

export const agentSchema = z.object({
  agent_name: z.string().min(1, 'Agent name is required'),
  description: z.string().optional(),
  agent_type: z.enum(['conversational', 'workflow']).optional(),
  trigger_type: z.enum(['manual', 'schedule', 'webhook', 'event']).optional(),
  status: z.string(),
  models: z.object({
    openai_chat: z.string().optional(),
    anthropic_chat: z.string().optional(),
    grok_chat: z.string().optional(),
    openai_realtime: z.string().optional(),
    grok_realtime: z.string().optional(),
    // legacy; still accepted by the backend for migration
    text: z.string().nullable().optional(),
    text_provider: z.string().nullable().optional(),
    realtime: z.string().nullable().optional(),
    realtime_provider: z.string().nullable().optional(),
  }),
  category: z.string().optional(),
  tags: z.array(z.string()).optional(),
  model_parameters: z.record(z.string(), z.unknown()).optional(),
  voice: z.record(z.string(), z.unknown()).optional(),
  // Free-form audio-mode prefix, prepended to the composed system prompt when the current turn
  // is voice (realtime + push-to-talk). Kept as one editable field so brevity / no-greeting /
  // no-filler rules live in one place per agent instead of being hardcoded in the backend.
  audio_prompt: z.string().optional(),
  system_prompts: z.array(systemPromptSchema).optional(),
  tools: z.array(z.any()).optional(),
  // Per-agent additive tool placement (Screens tab) on top of the code-derived scope.
  tool_scope_overrides: z.object({
    global_tools: z.array(z.string()),
    screens: z.record(z.string(), z.array(z.string())),
    // Core tools taken off the global list: still on the agent, no longer offered everywhere (shown as "Not placed").
    core_opt_out: z.array(z.string()).optional(),
  }).optional(),
  tenant_id: z.string().nullable().optional(),
  icon_url: z.string().optional(),
});

export type AgentFormValues = z.infer<typeof agentSchema>;

// ── prompt category config ─────────────────────────────────────────────────────

export const PROMPT_CATEGORIES: Record<PromptCategory, { label: string; chip: string; dot: string }> = {
  persona:  { label: 'Persona',  chip: 'bg-accent/10 text-accent-text border-accent/30', dot: 'bg-accent' },
  rules:    { label: 'Rules',    chip: 'bg-danger/10 text-danger-text border-danger/30',          dot: 'bg-danger' },
  context:  { label: 'Context',  chip: 'bg-accent/10 text-accent-text border-accent/30',       dot: 'bg-accent' },
  format:   { label: 'Format',   chip: 'bg-success/10 text-success-text border-success/30', dot: 'bg-success' },
  tools:    { label: 'Tools',    chip: 'bg-warning/10 text-warning-text border-warning/30',    dot: 'bg-warning' },
};

// ── screen (SamModule) metadata — display only ──────────────────────────────────
// The set of screens itself comes from the backend (useListScreensQuery) — this dict only supplies
// a friendly label/description/hue for known slugs. An unknown slug the backend returns still shows
// (falls back to the raw slug), so a new backend module is never silently hidden from this tab.

export type ScreenIconKind =
  | 'checklist' | 'calendar' | 'note' | 'chat' | 'people' | 'home'
  | 'flow' | 'bell' | 'camera' | 'clipboard' | 'receipt' | 'briefcase' | 'swap' | 'grid';

export interface ScreenMeta { label: string; description: string; hue: number; icon: ScreenIconKind }

export const SCREEN_META: Record<string, ScreenMeta> = {
  tasks:          { label: 'Tasks',          description: 'Task list & detail cards',                 hue: 217, icon: 'checklist' },
  my_todos:       { label: 'My To-Dos',      description: 'Mixed tasks + meetings widget',             hue: 199, icon: 'checklist' },
  meetings:       { label: 'Meetings',       description: 'Meeting list & detail cards',                hue: 262, icon: 'calendar' },
  notes:          { label: 'Notes',          description: 'Note capture & review cards',                hue: 291, icon: 'note' },
  communications: { label: 'Communications', description: 'Email / SMS compose & thread cards',         hue: 322, icon: 'chat' },
  contacts:       { label: 'Contacts',       description: 'Property contact cards',                     hue: 350, icon: 'people' },
  // Properties + Quick Quote merged into Workflow (one module for tool/prompt scoping and
  // screen-detection — see api-service/app/services/llm/module_aliases.py). The actual Property
  // detail card and Quick Quote card still render as their own UI panels; only the SAM-facing
  // scoping identity merged, so this is one card/config entry instead of three.
  workflow:       { label: 'Engagement', description: 'Engagement / workflow card — property detail, assessment, estimate, proposal, and quick quote all embedded in one surface.', hue: 43, icon: 'flow' },
  users:          { label: 'Users',          description: 'User directory',                             hue: 160, icon: 'people' },
  roles:          { label: 'Roles',          description: 'Role directory',                             hue: 172, icon: 'people' },
  calendar:       { label: 'Calendar',       description: 'Calendar panel',                              hue: 8,   icon: 'calendar' },
  notifications:  { label: 'Notifications',  description: 'Notification & approvals panel',              hue: 130, icon: 'bell' },
  assessments:    { label: 'Assessments',    description: 'Reserved — renders embedded in Workflow',      hue: 43,  icon: 'clipboard' },
  proposals:      { label: 'Proposals',      description: 'Reserved — renders embedded in Workflow',      hue: 43,  icon: 'clipboard' },
  transactions:   { label: 'Transactions',   description: 'Reserved — not yet a dedicated screen',        hue: 0,   icon: 'swap' },
  jobs:           { label: 'Jobs',           description: 'Reserved — not yet a dedicated screen',        hue: 0,   icon: 'briefcase' },
  billing:        { label: 'Billing',        description: 'Reserved — not yet a dedicated screen',        hue: 0,   icon: 'receipt' },
};

export function screenMeta(module: string): ScreenMeta {
  return SCREEN_META[module] ?? { label: module, description: '', hue: (module.length * 47) % 360, icon: 'grid' };
}

export const TENANT_ROLES: { value: string; label: string }[] = [
  { value: 'tenant_admin',     label: 'Tenant Admin' },
  { value: 'sales_agent',      label: 'Sales Agent' },
  { value: 'project_manager',  label: 'Project Manager' },
  { value: 'office_admin',     label: 'Office Admin' },
  { value: 'crew_member',      label: 'Crew Member' },
];

export const emptyAgent: AgentFormValues = {
  agent_name: '',
  description: '',
  models: {
    openai_chat: 'gpt-5-mini',
    anthropic_chat: 'claude-sonnet-5-5',
    grok_chat: 'grok-4.7',
    openai_realtime: 'gpt-realtime-2',
    grok_realtime: 'grok-voice-latest',
    text: null,
    text_provider: null,
    realtime: null,
    realtime_provider: null,
  },
  status: 'active',
  tenant_id: null,
  tools: [],
  tool_scope_overrides: { global_tools: [], screens: {} },
  audio_prompt: '',
  system_prompts: [],
  model_parameters: {},
  voice: {},
  tags: [],
  category: '',
};

// ── SectionCard ────────────────────────────────────────────────────────────────

export const SectionCard: React.FC<{
  title?: string;
  desc?: string;
  children: React.ReactNode;
  right?: React.ReactNode;
  className?: string;
}> = ({ title, desc, children, right, className }) => (
  <div className={cn('card-surface mb-5', className)}>
    {(title || right) && (
      <div className="flex items-start justify-between px-5 pt-5 pb-0">
        <div>
          {title && <h2 className="text-sm font-semibold text-ui-text">{title}</h2>}
          {desc && <p className="text-xs text-ui-text3 mt-0.5">{desc}</p>}
        </div>
        {right}
      </div>
    )}
    <div className="px-5 py-4">{children}</div>
  </div>
);

// ── Toggle ─────────────────────────────────────────────────────────────────────

export const Toggle: React.FC<{ on: boolean; onChange: () => void; disabled?: boolean; label?: string }> = ({ on, onChange, disabled, label }) => (
  <Switch checked={on} onCheckedChange={() => onChange()} disabled={disabled} aria-label={label} />
);

// ── JsonField ──────────────────────────────────────────────────────────────────

export const JsonField: React.FC<{
  value: unknown;
  onChange: (v: unknown) => void;
  rows?: number;
  placeholder?: string;
}> = ({ value, onChange, rows = 4, placeholder }) => {
  const [text, setText] = useState<string>(() =>
    typeof value === 'string' ? value : JSON.stringify(value ?? {}, null, 2)
  );
  const [bad, setBad] = useState(false);

  // Re-sync the editable text when `value` changes from OUTSIDE this field — e.g. a "click to
  // insert" chip elsewhere on the page, or a draft reset. Without this, the field only ever
  // echoes its own onChange and silently ignores every later external update to `value`, which
  // made "click to insert" chips built on top of it a no-op (found via browser verification).
  // Skipped while the current text fails to parse — that's the user's in-progress edit, and an
  // unrelated re-render must not clobber it.
  useEffect(() => {
    let parsed: unknown;
    try {
      parsed = text.trim() === '' ? {} : JSON.parse(text);
    } catch {
      return;
    }
    if (JSON.stringify(parsed) !== JSON.stringify(value ?? {})) {
      setText(typeof value === 'string' ? value : JSON.stringify(value ?? {}, null, 2));
      setBad(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <>
      <textarea
        className={cn(areaCls, 'font-mono', bad && 'border-danger focus:border-danger focus:ring-danger/20')}
        rows={rows}
        value={text}
        placeholder={placeholder}
        onChange={(e) => {
          setText(e.target.value);
          try { onChange(JSON.parse(e.target.value || '{}')); setBad(false); }
          catch { setBad(true); }
        }}
      />
      {bad && <p className="mt-1 text-xs text-danger-text">Invalid JSON — last valid value kept.</p>}
    </>
  );
};

// ── Modal ──────────────────────────────────────────────────────────────────────

export const Modal: React.FC<{
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  wide?: boolean;
}> = ({ title, onClose, children, footer, wide }) => (
  <BaseModal title={title} onClose={onClose} footer={footer} maxWidthClassName={wide ? 'max-w-2xl' : 'max-w-lg'} bodyClassName="space-y-0">
    {children}
  </BaseModal>
);

// ── AgentTypeBadge ─────────────────────────────────────────────────────────────

export const AgentTypeBadge: React.FC<{ type?: string }> = ({ type }) => {
  if (!type) return null;
  const variant: Record<string, BadgeProps['variant']> = {
    conversational: 'accent',
    workflow: 'warning',
  };
  const label: Record<string, string> = {
    conversational: 'Chat',
    workflow: 'Workflow',
  };
  return (
    <Badge variant={variant[type] ?? 'neutral'}>
      {label[type] ?? type}
    </Badge>
  );
};

// ── IngestionDot ───────────────────────────────────────────────────────────────

export const IngestionDot: React.FC<{ status?: string }> = ({ status }) => {
  const dotCls: Record<string, string> = {
    pending: 'bg-steel',
    processing: 'bg-warning animate-pulse',
    completed: 'bg-success',
    failed: 'bg-danger',
  };
  const label: Record<string, string> = {
    pending: 'Pending', processing: 'Processing…', completed: 'Indexed', failed: 'Failed',
  };
  if (!status) return null;
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={cn('h-2 w-2 rounded-full shrink-0', dotCls[status] ?? 'bg-ui-border3')} />
      <span className="text-xs text-ui-text2">{label[status] ?? status}</span>
    </span>
  );
};

// ── RunStatusPill ──────────────────────────────────────────────────────────────

import { AgentRun } from '@/redux/internal-services/agentsApi';

export const RunStatusPill: React.FC<{ status: AgentRun['status'] }> = ({ status }) => {
  const variant: Record<string, BadgeProps['variant']> = {
    pending: 'neutral',
    running: 'accent',
    completed: 'success',
    error: 'danger',
    timeout: 'warning',
  };
  return (
    <Badge variant={variant[status] ?? 'neutral'}>
      {status}
    </Badge>
  );
};

// ── DescriptorField ────────────────────────────────────────────────────────────

export const DescriptorField: React.FC<{
  d: ParameterDescriptor;
  value: unknown;
  onChange: (v: unknown) => void;
}> = ({ d, value, onChange }) => {
  if (d.type === 'bool') {
    return (
      <label className="flex items-center gap-2 cursor-pointer py-1">
        <input
          type="checkbox"
          checked={Boolean(value)}
          onChange={(e) => onChange(e.target.checked)}
          className="h-4 w-4 accent-accent"
        />
        <span className="text-sm text-ui-text">{d.label}</span>
      </label>
    );
  }
  return (
    <div>
      <label className={labelCls}>{d.label}</label>
      {d.type === 'enum' && d.options ? (
        <select className={inputCls} value={String(value ?? d.default ?? '')} onChange={(e) => onChange(e.target.value)}>
          {d.options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : d.type === 'list' ? (
        <input
          className={inputCls}
          value={Array.isArray(value) ? (value as string[]).join(', ') : ''}
          onChange={(e) => onChange(e.target.value.split(',').map((s) => s.trim()).filter(Boolean))}
          placeholder="comma,separated"
        />
      ) : d.type === 'int' || d.type === 'float' ? (
        <input
          className={inputCls}
          type="number"
          min={d.min}
          max={d.max}
          step={d.type === 'float' ? 0.1 : 1}
          value={value === undefined || value === null ? '' : String(value)}
          onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
        />
      ) : (
        <input className={inputCls} value={String(value ?? '')} onChange={(e) => onChange(e.target.value)} />
      )}
    </div>
  );
};

// ── FeatureFlagsEditor ─────────────────────────────────────────────────────────

export const FeatureFlagsEditor: React.FC<{
  flags: Record<string, unknown>;
  onChange: (flags: Record<string, unknown>) => void;
}> = ({ flags, onChange }) => {
  const [newKey, setNewKey] = useState('');
  const entries = Object.entries(flags);
  const update = (key: string, val: unknown) => onChange({ ...flags, [key]: val });
  const remove = (key: string) => {
    const next = { ...flags };
    delete next[key];
    onChange(next);
  };
  const addFlag = () => {
    const k = newKey.trim().replace(/\s+/g, '_');
    if (!k || k in flags) return;
    onChange({ ...flags, [k]: false });
    setNewKey('');
  };
  return (
    <div className="space-y-2">
      {entries.length === 0 && <p className="text-xs text-ui-text3">No feature flags defined.</p>}
      {entries.map(([k, v]) => (
        <div key={k} className="flex items-center gap-3 rounded-control border border-ui-border px-3 py-2">
          <code className="flex-1 font-mono text-xs text-ui-text">{k}</code>
          {typeof v === 'boolean' ? (
            <Toggle on={v} onChange={() => update(k, !v)} label={k} />
          ) : (
            <input className={cn(fieldClass({ size: 'sm' }), 'w-32 text-xs')} value={String(v ?? '')} onChange={(e) => update(k, e.target.value)} />
          )}
          <Button type="button" variant="destructive-ghost" size="xs" onClick={() => remove(k)} className="shrink-0">Remove</Button>
        </div>
      ))}
      <div className="flex gap-2 pt-1">
        <input
          className={cn(inputCls, 'flex-1')}
          placeholder="flag_key"
          value={newKey}
          onChange={(e) => setNewKey(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && addFlag()}
        />
        <Button type="button" variant="soft" size="sm" onClick={addFlag} className="shrink-0">
          Add flag
        </Button>
      </div>
    </div>
  );
};
